import { RefObject, useCallback, useState } from 'react';

const MIN_PANEL_WIDTH_PX = 240;
const MIN_PANEL_HEIGHT_PX = 112;

/** Same id as `StyledCreateAudienceModalContent` (full modal row including taxonomy). */
const CREATE_AUDIENCE_MODAL_ROW_ID = 'gtCreateAudienceModalRow';

type HorizontalHandleEdge = 'left' | 'right';
type VerticalHandleEdge = 'top' | 'bottom';

export type UsePanelResizeOptions =
  | {
      axis: 'horizontal';
      handleEdge: HorizontalHandleEdge;
      initialSizePct?: number;
      minSizePx?: number;
    }
  | {
      axis: 'vertical';
      handleEdge: VerticalHandleEdge;
      initialSizePct?: number;
      minSizePx?: number;
    };

const AXIS_CONFIG = {
  horizontal: {
    defaultMinSizePx: MIN_PANEL_WIDTH_PX,
    getPointerCoord: (clientX: number, _: number) => clientX,
    getPanelSize: (rect: DOMRect) => rect.width,
    getHostSize: (rect: DOMRect) => rect.width,
    isPositiveEdge: (edge: HorizontalHandleEdge | VerticalHandleEdge) =>
      edge === 'right',
  },
  vertical: {
    defaultMinSizePx: MIN_PANEL_HEIGHT_PX,
    getPointerCoord: (_: number, clientY: number) => clientY,
    getPanelSize: (rect: DOMRect) => rect.height,
    getHostSize: (rect: DOMRect) => rect.height,
    isPositiveEdge: (edge: HorizontalHandleEdge | VerticalHandleEdge) =>
      edge === 'bottom',
  },
} as const;

/**
 * Panel width or height as % of host (`offsetParent` or create-audience modal row),
 * driven by a resize handle on the configured edge.
 */
export function usePanelResize(
  expanded: boolean,
  panelRef: RefObject<HTMLDivElement | null>,
  options: UsePanelResizeOptions
) {
  const { axis, handleEdge, initialSizePct = 100, minSizePx } = options;
  const minSize = minSizePx ?? AXIS_CONFIG[axis].defaultMinSizePx;

  const [panelSizePct, setPanelSizePct] = useState(initialSizePct);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!expanded) return;
      e.preventDefault();
      e.stopPropagation();

      const axisConfig = AXIS_CONFIG[axis];
      const handle = e.currentTarget;
      const panel = panelRef.current;
      const host =
        (panel?.offsetParent as HTMLElement | null) ||
        (typeof document !== 'undefined'
          ? document.getElementById(CREATE_AUDIENCE_MODAL_ROW_ID)
          : null);
      if (!panel || !host) return;

      const pointerId = e.pointerId;
      handle.setPointerCapture(pointerId);

      const startPointer = axisConfig.getPointerCoord(e.clientX, e.clientY);
      const startSize = axisConfig.getPanelSize(panel.getBoundingClientRect());
      const hostSize = axisConfig.getHostSize(host.getBoundingClientRect());

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        const delta =
          axisConfig.getPointerCoord(ev.clientX, ev.clientY) - startPointer;
        const signedDelta = axisConfig.isPositiveEdge(handleEdge)
          ? delta
          : -delta;
        const newSize = Math.min(
          hostSize,
          Math.max(minSize, startSize + signedDelta)
        );
        setPanelSizePct((newSize / hostSize) * 100);
      };

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        try {
          handle.releasePointerCapture(pointerId);
        } catch {
          /* capture already released */
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [expanded, panelRef, handleEdge, axis, minSize]
  );

  return { panelSizePct, setPanelSizePct, handleResizePointerDown };
}
